import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { renderUnitPortrait } from '../components/UnitPortrait';
import { getFullSpriteUrl } from '../../combat/spriteConfig';
import { Tooltip } from '../components/Tooltip';
import { buildUnitTooltip, buildItemTooltip, buildSkillAbilityLines, rarityColor } from '../components/TooltipContent';
import { renderAffixesCompact } from '../components/AffixDisplay';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { elementColor } from '../../utils/elementColors';
import { renderItemIcon, renderElementIcon, renderMasteryIcon, getAbilityIconUrl } from '../components/ItemIcon';
import { formatUnitClass, formatRace, formatDefensiveTypes, formatElement, formatCategory } from '../components/GameTerms';
import { renderEquipCard, formatCompactAffix } from '../components/GearCard';
import { EventBus, Events } from '../../managers';
import type { UnitData, ItemData, ConfigData, PartyUnit, AffixInstance, AffixDef, ComputedStats, SkillEstimate, SkillDef, MasteryDef, MasteryCategory, MasteryRequirement, NotificationData } from '../../types';
import { SKILL_SLOT_KEYS } from '../../types';

/** Memory currency display order (low → high) and per-tier XP grant.
 *  Must match the server-side MEMORY_XP_BY_TIER in `src/services/unitService.ts`. */
const MEMORY_TIERS = [
  { id: 'faintMemory',    name: 'Faint Memory',    xp: 75,   accent: '#94a3b8' },
  { id: 'stirringMemory', name: 'Stirring Memory', xp: 300,  accent: '#60a5fa' },
  { id: 'brightMemory',   name: 'Bright Memory',   xp: 1050, accent: '#fcd34d' },
  { id: 'radiantMemory',  name: 'Radiant Memory',  xp: 3000, accent: '#fbbf24' },
] as const;


export class PartyScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'party',
    name: 'Party',
    persistent: false,
    needsPhaser: false,
  };

  private selectedUnitId: string | null = null;

  // Mastery state
  private pendingMasteryPoints: Record<string, number> = {};
  private masteryFilterRecommended: boolean = localStorage.getItem('mastery_filter_recommended') !== 'false';
  private activeDetailTab: 'masteries' | 'traits' | 'stats' | 'memory' = 'masteries';

  // Skill equip state — tap slot to prime, tap unlocked skill to equip
  private primedSkillSlot: number | null = null;

  // Drag state
  private dragSource: { type: 'inventory-item'; itemId: string; invSlot: number; category: 'weapon' | 'armor' | 'accessory' | 'offhand' } | { type: 'equip-slot'; slot: 'weapon' | 'armor' | 'accessory' | 'offhand' } | null = null;

  constructor() {
    super({ rootId: 'party-screen-root' });
  }

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'party-screen-root';
    const isLocalhost = window.location.hostname === 'localhost';
    const adminBtn = isLocalhost
      ? '<button class="party-admin-levelup-btn" type="button" title="Admin: level up selected strand">+1 Lvl</button>'
      : '';
    root.innerHTML = `
      <div class="party-screen">
        ${screenHeaderHTML('Strands', adminBtn)}
        <div class="party-body">
          <div class="hero-roster-grid"></div>
          <div class="unit-panel">
            <div class="unit-detail"></div>
          </div>
          <div class="gear-column">
            <div class="equip-section">
              <div class="section-label"><span>Skills</span></div>
              <div class="skill-slots"></div>
            </div>
            <div class="gear-view">
              <div class="equip-section">
                <div class="section-label"><span>Equipment</span></div>
                <div class="equip-slots"></div>
              </div>
              <div class="inventory-section">
                <div class="section-label"><span>Inventory</span></div>
                <div class="inv-tabs">
                  <button class="inv-toggle-btn" type="button">Show empty</button>
                </div>
                <div class="inventory-grid"></div>
                <div class="inv-detail-bar"></div>
              </div>
            </div>
            <div class="skill-browser hidden"></div>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    this.$('.party-admin-levelup-btn')?.addEventListener('click', () => this.adminLevelUpSelected());

    // Inventory toggle (mobile: show/hide empty slots)
    this.$('.inv-toggle-btn')?.addEventListener('click', () => {
      const grid = this.$('.inventory-grid');
      if (!grid) return;
      const expanded = grid.classList.toggle('inventory-expanded');
      const btn = this.$('.inv-toggle-btn');
      if (btn) btn.textContent = expanded ? 'Hide empty' : 'Show empty';
    });

    // Drop on inventory grid area → unequip
    const invGrid = this.$('.inventory-grid');
    invGrid?.addEventListener('dragover', (e) => {
      const src = this.dragSource;
      if (!src) return;
      if (src.type === 'equip-slot') {
        e.preventDefault();
        (e as DragEvent).dataTransfer!.dropEffect = 'move';
        invGrid.classList.add('drop-target');
      }
    });
    invGrid?.addEventListener('dragleave', (e) => {
      // Only remove highlight if leaving the grid entirely (not entering a child)
      if (!invGrid.contains(e.relatedTarget as Node)) {
        invGrid.classList.remove('drop-target');
      }
    });
    invGrid?.addEventListener('drop', (e) => {
      e.preventDefault();
      invGrid.classList.remove('drop-target');
      if (!this.dragSource || !this.selectedUnitId) return;
      if (this.dragSource.type === 'equip-slot') {
        this.unequipSlot(this.selectedUnitId, this.dragSource.slot);
      }
      this.dragSource = null;
    });
  }

  protected setupSubscriptions(): void {
    this.subscribeState('unitData', () => this.onRender());
    this.subscribeState('itemData', () => this.onRender());
    this.subscribeState('configData', () => this.onRender());
    this.subscribeState('notificationData', () => this.onRender());
    this.subscribeState('playerData', () => this.onRender());
  }

  private cameFromHeroSelect = false;

  onEnter(params?: Record<string, unknown>): void {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const focusId = params?.focusUnitId as string | undefined;
    this.cameFromHeroSelect = !!focusId;

    if (focusId && unitData?.roster?.find(u => u.id === focusId)) {
      this.selectedUnitId = focusId;
    } else if (unitData?.party?.length) {
      this.selectedUnitId = unitData.party[0].id;
    } else if (unitData?.roster?.length) {
      this.selectedUnitId = unitData.roster[0].id;
    }
    this.pendingMasteryPoints = {};
    this.onRender();
  }

  protected onRender(): void {
    // Preserve scroll position across DOM rebuilds
    const scrollEl = this.rootEl?.querySelector('.party-body') as HTMLElement | null;
    const savedScroll = scrollEl?.scrollTop ?? 0;

    if (this.rootEl) renderScreenCurrencies(this.rootEl, 'party');
    this.renderBackButton();
    this.renderHeroRoster();
    this.renderUnitDetail();
    this.renderSkillSlots();
    this.renderEquipSlots();
    this.renderInventoryGrid();

    const browser = this.$('.skill-browser');
    if (browser && !browser.classList.contains('hidden')) {
      this.renderSkillBrowser();
    }

    // Restore scroll position after render
    if (scrollEl) scrollEl.scrollTop = savedScroll;
  }

  private renderBackButton(): void {
    if (!this.rootEl) return;
    const header = this.rootEl.querySelector('.screen-header') as HTMLElement | null;
    if (!header) return;

    const existing = header.querySelector('.party-back-btn');
    if (this.cameFromHeroSelect && !existing) {
      const btn = document.createElement('button');
      btn.className = 'party-back-btn';
      btn.textContent = '\u2190 Deploy';
      btn.addEventListener('click', () => ScreenRouter.back());
      header.insertBefore(btn, header.firstChild);
    } else if (!this.cameFromHeroSelect && existing) {
      existing.remove();
    }
  }

  // ─── Helpers ───

  private getSelectedUnit(): PartyUnit | null {
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    return unitData?.roster?.find(u => u.id === this.selectedUnitId) ?? null;
  }

  private unitMeetsMasteryReqs(unit: PartyUnit, reqs?: MasteryRequirement[]): boolean {
    if (!reqs || reqs.length === 0) return true;
    const ranks = unit.computedStats.masteryRanks;
    return reqs.every(r => (ranks[r.masteryId] ?? 0) >= r.rank);
  }

  /** Check if an item is blocked by weapon-pair rules (held offhand + 2h, quiver without bow). */
  private isHandednessBlocked(unit: PartyUnit, item: import('../../types').ItemData, configData: import('../../types').ConfigData | undefined): boolean {
    const baseDef = configData?.itemBases?.[item.baseType];
    if (!baseDef) return false;
    // Held offhand blocked by unit's 2h weapon
    if (baseDef.category === 'offhand' && baseDef.grip === 'held' && unit.weaponId) {
      const weaponBase = configData?.itemBases?.[this.getEquippedBaseType(unit, 'weapon')];
      if (weaponBase?.category === 'weapon' && weaponBase.handedness === '2h') return true;
    }
    // Quivered offhand requires a bow in the mainhand
    if (baseDef.category === 'offhand' && baseDef.grip === 'quivered') {
      const weaponBase = configData?.itemBases?.[this.getEquippedBaseType(unit, 'weapon')];
      if (!weaponBase || weaponBase.category !== 'weapon' || weaponBase.family !== 'bow') return true;
    }
    return false;
  }

  private getEquippedBaseType(unit: PartyUnit, slot: string): string {
    const items = StateManager.get('itemData') as import('../../types').ItemData[] | undefined;
    if (!items) return '';
    const slotId = slot === 'weapon' ? unit.weaponId : slot === 'offhand' ? unit.offhandId : '';
    if (!slotId) return '';
    const item = items.find((i: import('../../types').ItemData) => i.id === slotId);
    return item?.baseType ?? '';
  }

  private getSpriteKey(unit: PartyUnit): string {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    // Skin override
    if (unit.activeSkinId && configData?.skins?.[unit.activeSkinId]) {
      return configData.skins[unit.activeSkinId].spriteKey;
    }
    return configData?.unitTemplates?.[unit.unitClass]?.spriteKey ?? 'warrior';
  }

  private getSkinsForUnit(unit: PartyUnit): { id: string; name: string; spriteKey: string; owned: number; inUse: number }[] {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const unitData = StateManager.get('unitData') as UnitData | undefined;
    if (!configData?.skins) return [];

    const ownedSkins = unitData?.ownedSkins ?? {};
    const roster = unitData?.roster ?? [];

    return Object.values(configData.skins)
      .filter(s => s.unitClasses.includes(unit.unitClass) && (ownedSkins[s.id] ?? 0) > 0)
      .map(s => {
        const owned = ownedSkins[s.id] ?? 0;
        const inUse = roster.filter(u => u.activeSkinId === s.id).length;
        return { id: s.id, name: s.name, spriteKey: s.spriteKey, owned, inUse };
      });
  }

  private itemAffixSummary(item: ItemData): string {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const parts: string[] = [];

    try {
      const implicit: AffixInstance = JSON.parse(item.implicitAffix);
      const def: AffixDef | undefined = configData?.affixes?.[implicit.affixId];
      if (def) {
        parts.push(formatCompactAffix(implicit, def));
      }
    } catch { /* ignore */ }

    try {
      const explicits: AffixInstance[] = JSON.parse(item.explicitAffixes);
      for (const inst of explicits) {
        const def: AffixDef | undefined = configData?.affixes?.[inst.affixId];
        if (def) {
          parts.push(formatCompactAffix(inst, def));
        }
      }
    } catch { /* ignore */ }

    return parts.join(', ');
  }

  // ─── Hero Roster Grid ───

  private renderHeroRoster(): void {
    const gridEl = this.$('.hero-roster-grid');
    if (!gridEl) return;

    const unitData = StateManager.get('unitData') as UnitData | undefined;
    const roster = unitData?.roster ?? [];

    // Get strand-level alerts from notification state
    const notifState = StateManager.get('notificationData') as NotificationData | null;
    const strandAlerts = notifState?.strandAlerts ?? {};

    gridEl.innerHTML = roster.map(unit => {
      const spriteKey = this.getSpriteKey(unit);
      const isSelected = unit.id === this.selectedUnitId;
      const rColor = rarityColor(unit.rarity ?? 'normal');
      const showDot = !!strandAlerts[unit.id];
      return `
        <div class="hero-roster-card ${isSelected ? 'selected' : ''}" data-unit-id="${unit.id}">
          <div class="hero-roster-portrait">${renderUnitPortrait({ spriteKey, name: unit.name, size: 'sm' })}</div>
          ${showDot ? '<span class="hero-roster-dot"></span>' : ''}
          <span class="hero-roster-name" style="color:${rColor}">${unit.name}</span>
          <span class="hero-roster-level">Lv.${unit.level ?? 1}</span>
        </div>
      `;
    }).join('');

    // Bind clicks — clear strand alert on click
    gridEl.querySelectorAll('.hero-roster-card').forEach(el => {
      const cardEl = el as HTMLElement;
      const unitId = cardEl.dataset.unitId!;
      cardEl.addEventListener('click', () => {
        if (strandAlerts[unitId]) {
          APIManager.clearNotification('strand', unitId);
          // Optimistically update local state
          const updated = { ...(strandAlerts || {}) };
          delete updated[unitId];
          StateManager.set('notificationData', {
            newItems: notifState?.newItems ?? false,
            newStrands: notifState?.newStrands ?? false,
            strandAlerts: updated,
          });
        }
        this.selectedUnitId = unitId;
        this.pendingMasteryPoints = {};
        this.primedSkillSlot = null;
        if (this.activeDetailTab === 'memory') this.activeDetailTab = 'masteries';
        this.onRender();
      });
    });
  }

  // ─── Unit Detail ───

  private renderUnitDetail(): void {
    const detailEl = this.$('.unit-detail');
    if (!detailEl) return;

    const unit = this.getSelectedUnit();
    const configData = StateManager.get('configData') as ConfigData | undefined;

    if (!unit) {
      detailEl.innerHTML = '<div class="unit-detail-empty">Select a party member</div>';
      return;
    }

    const spriteKey = this.getSpriteKey(unit);
    const rColor = rarityColor(unit.rarity ?? 'normal');
    const className = unit.unitClass ? formatUnitClass(unit.unitClass) : '';
    const race = unit.race ? formatRace(unit.race) : '';
    const defensiveLabel = formatDefensiveTypes(unit.defensiveTypes ?? []);
    const cs = unit.computedStats;
    const apBadge = ''; // Mastery points shown in tab badge + mastery section header

    const availableSkins = this.getSkinsForUnit(unit);
    const hasSkins = availableSkins.length > 0;

    detailEl.innerHTML = `
      <div class="unit-detail-top">
        <div class="unit-detail-portrait-wrap${hasSkins ? ' has-skins' : ''}">
          ${renderUnitPortrait({ spriteKey, name: unit.name, size: 'md', className: 'unit-detail-portrait' })}
          ${hasSkins ? '<div class="skin-indicator" title="Change skin"></div>' : ''}
        </div>
        <div class="unit-detail-info">
          <div class="unit-detail-name-row">
            <span class="unit-detail-name" style="color:${rColor}">${unit.name}</span>
          </div>
          <span class="unit-detail-meta">${className} · ${race}${defensiveLabel ? ` · ${defensiveLabel}` : ''}</span>
          <span class="unit-detail-level">Lv.${unit.level ?? 1}</span>
          ${this.renderXpBar(unit)}
          <div class="unit-detail-stats">
            <div class="stat-pair"><span class="stat-label">Health</span><span class="stat-value">${cs.hp}</span></div>
          </div>
        </div>
      </div>
      <div class="unit-detail-tabs">
        <button class="unit-tab${this.activeDetailTab === 'masteries' ? ' active' : ''}" data-tab="masteries">Masteries${(unit.unspentMasteryPoints ?? 0) > 0 ? ' <span class="tab-badge">' + unit.unspentMasteryPoints + '</span>' : ''}</button>
        <button class="unit-tab${this.activeDetailTab === 'traits' ? ' active' : ''}" data-tab="traits">Traits</button>
        <button class="unit-tab${this.activeDetailTab === 'stats' ? ' active' : ''}" data-tab="stats">Stats</button>
        <button class="unit-tab${this.activeDetailTab === 'memory' ? ' active' : ''}" data-tab="memory">Memory${this.totalMemoryOwned() > 0 ? ' <span class="tab-badge">' + this.totalMemoryOwned() + '</span>' : ''}</button>
      </div>
      <div class="unit-detail-tab-content">
        ${this.activeDetailTab === 'masteries' ? this.renderMasterySection(unit, cs) : ''}
        ${this.activeDetailTab === 'traits' ? this.renderAffixSection(unit, configData) : ''}
        ${this.activeDetailTab === 'stats' ? this.renderStatsPanel(cs) : ''}
        ${this.activeDetailTab === 'memory' ? this.renderMemoryPicker(unit) : ''}
      </div>
      ${this.activeDetailTab === 'masteries' ? this.renderPendingBar(unit) : ''}
    `;

    // Tooltip on portrait
    const portrait = detailEl.querySelector('.unit-detail-portrait') as HTMLElement;
    if (portrait) {
      Tooltip.attach(portrait, () => buildUnitTooltip(unit, configData));
    }

    // Skin picker on portrait click (only if skins available)
    if (hasSkins) {
      const portraitWrap = detailEl.querySelector('.unit-detail-portrait-wrap') as HTMLElement;
      portraitWrap?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openSkinPicker(unit, availableSkins);
      });
    }

    // Wire up Memory tab events when active.
    if (this.activeDetailTab === 'memory') this.bindMemoryPickerEvents();

    // Tab switching
    detailEl.querySelectorAll('.unit-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const next = (tab as HTMLElement).dataset.tab as 'masteries' | 'traits' | 'stats' | 'memory';
        // Initialize memory pending counts (default 1 per owned tier) on entry.
        if (next === 'memory' && this.activeDetailTab !== 'memory') {
          const player = StateManager.get('playerData');
          this.memoryPickerCounts = {};
          for (const tier of MEMORY_TIERS) {
            const owned = player ? ((player as any)[tier.id] ?? 0) as number : 0;
            this.memoryPickerCounts[tier.id] = owned > 0 ? 1 : 0;
          }
        }
        this.activeDetailTab = next;
        this.renderUnitDetail();
      });
    });

    // Mastery invest buttons
    this.bindMasteryButtons();

    // Resistance row tooltips
    this.bindResistanceTooltips();
  }

  // ─── Skin Picker ───

  private openSkinPicker(unit: PartyUnit, skins: { id: string; name: string; spriteKey: string; owned: number; inUse: number }[]): void {
    // Remove existing picker
    document.querySelector('.skin-picker-overlay')?.remove();

    const configData = StateManager.get('configData') as ConfigData | undefined;
    const baseSpriteKey = configData?.unitTemplates?.[unit.unitClass]?.spriteKey ?? 'warrior';

    const overlay = document.createElement('div');
    overlay.className = 'skin-picker-overlay';

    // Build skin cards — "Default" first, then available skins
    const allOptions = [
      { id: null as string | null, name: 'Default', spriteKey: baseSpriteKey },
      ...skins,
    ];

    const cardsHTML = allOptions.map(s => {
      const isActive = s.id === null ? !unit.activeSkinId : unit.activeSkinId === s.id;
      const imgUrl = getFullSpriteUrl(s.spriteKey);
      const skinData = s.id ? skins.find(sk => sk.id === s.id) : null;
      const available = !s.id || isActive || !skinData || skinData.inUse < skinData.owned;
      const countLabel = skinData ? `${skinData.owned - skinData.inUse + (isActive ? 1 : 0)}/${skinData.owned}` : '';
      return `
        <div class="skin-card${isActive ? ' active' : ''}${!available ? ' unavailable' : ''}" data-skin-id="${s.id ?? ''}">
          <div class="skin-card-img"><img src="${imgUrl}" alt="${s.name}"></div>
          <span class="skin-card-name">${s.name}</span>
          ${countLabel ? `<span class="skin-card-count">${countLabel}</span>` : ''}
        </div>
      `;
    }).join('');

    overlay.innerHTML = `
      <div class="skin-picker-panel">
        <div class="skin-picker-header">
          <span class="skin-picker-title">Skins</span>
          <button class="skin-picker-close">&times;</button>
        </div>
        <div class="skin-picker-grid">${cardsHTML}</div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Close handlers
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
    overlay.querySelector('.skin-picker-close')?.addEventListener('click', () => overlay.remove());

    // Skin selection
    overlay.querySelectorAll('.skin-card').forEach(card => {
      card.addEventListener('click', async () => {
        const skinId = (card as HTMLElement).dataset.skinId || null;
        overlay.remove();
        await this.applySkin(unit.id, skinId);
      });
    });
  }

  // ─── Memory Tab ───

  /** Per-tier selected quantity inside the Memory tab. Defaults each tier
   *  to 1 (or 0 if none owned) when the tab is entered. */
  private memoryPickerCounts: Record<string, number> = {};

  private getOwned(tier: string): number {
    const player = StateManager.get('playerData');
    return player ? ((player as any)[tier] ?? 0) as number : 0;
  }

  private totalMemoryOwned(): number {
    return MEMORY_TIERS.reduce((s, t) => s + this.getOwned(t.id), 0);
  }

  /** Update a single tier row in place (count, spend button label, disabled
   *  states) without re-rendering the whole tab. */
  private updateMemoryRow(root: Element, tier: string): void {
    const row = root.querySelector(`.memory-tier-row[data-tier="${tier}"]`) as HTMLElement | null;
    if (!row) return;
    const tierDef = MEMORY_TIERS.find(t => t.id === tier);
    if (!tierDef) return;

    const owned = this.getOwned(tier);
    const count = this.memoryPickerCounts[tier] ?? 0;
    const xpPreview = count * tierDef.xp;

    const countEl = row.querySelector('.step-count') as HTMLElement | null;
    if (countEl) countEl.textContent = String(count);

    const ownedEl = row.querySelector('.memory-tier-owned') as HTMLElement | null;
    if (ownedEl) ownedEl.textContent = String(owned);

    const dec = row.querySelector('[data-step="-1"]') as HTMLButtonElement | null;
    const inc = row.querySelector('[data-step="1"]') as HTMLButtonElement | null;
    const max = row.querySelector('[data-step-max]') as HTMLButtonElement | null;
    if (dec) dec.disabled = count <= 0;
    if (inc) inc.disabled = count >= owned;
    if (max) max.disabled = owned <= 0 || count >= owned;

    const spend = row.querySelector('[data-spend-tier]') as HTMLButtonElement | null;
    if (spend) {
      spend.disabled = count <= 0;
      spend.textContent = count > 0 ? `Gain +${xpPreview} XP` : 'Gain XP';
    }

    row.classList.toggle('memory-tier-empty', owned <= 0);
  }

  private renderMemoryPicker(unit: PartyUnit): string {
    const atLevelCap = (unit.xpToNextLevel ?? 0) <= 0;

    if (atLevelCap) {
      return `
        <div class="stats-section">
          <div class="section-label"><span>Memories</span></div>
          <div class="memory-empty-body">${unit.name} is at max level.</div>
        </div>
      `;
    }

    // Only render rows for tiers the player actually owns. Empty inventory is
    // covered by the hint message below the section label.
    const tiersHtml = MEMORY_TIERS
      .filter(tier => this.getOwned(tier.id) > 0)
      .map(tier => {
        const owned = this.getOwned(tier.id);
        const count = this.memoryPickerCounts[tier.id] ?? 0;
        const xpPreview = count * tier.xp;
        const decDis = count <= 0;
        const incDis = count >= owned;
        const maxDis = count >= owned;
        const spendDis = count <= 0;
        return `
          <div class="memory-tier-row" data-tier="${tier.id}" style="--tier-accent:${tier.accent}">
            <div class="memory-tier-info">
              <span class="memory-tier-name">${tier.name}</span>
              <span class="memory-tier-meta">+${tier.xp} XP each · <strong class="memory-tier-owned">${owned}</strong> owned</span>
            </div>
            <div class="memory-tier-stepper">
              <button class="step-btn" data-step="-1" ${decDis ? 'disabled' : ''}>−</button>
              <span class="step-count">${count}</span>
              <button class="step-btn" data-step="1" ${incDis ? 'disabled' : ''}>+</button>
              <button class="step-btn step-max" data-step-max ${maxDis ? 'disabled' : ''}>Max</button>
            </div>
            <button class="memory-spend-btn" data-spend-tier="${tier.id}" ${spendDis ? 'disabled' : ''}>${count > 0 ? `Gain +${xpPreview} XP` : 'Gain XP'}</button>
          </div>
        `;
      }).join('');

    const hint = this.totalMemoryOwned() === 0
      ? `<div class="memory-empty-body">Memories drop from chapter completions and crawls. Spend them here to grant XP to this strand.</div>`
      : '';

    return `
      <div class="stats-section">
        <div class="section-label"><span>Memories</span></div>
        ${hint}
        <div class="memory-section-tiers">${tiersHtml}</div>
      </div>
    `;
  }

  /** Bind tab events. Called after every renderUnitDetail when Memory tab is active. */
  private bindMemoryPickerEvents(): void {
    if (!this.rootEl) return;
    const root = this.rootEl.querySelector('.unit-detail-tab-content') as HTMLElement | null;
    if (!root) return;

    root.querySelectorAll('[data-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.memory-tier-row') as HTMLElement | null;
        const tier = row?.dataset.tier;
        if (!tier) return;
        const owned = this.getOwned(tier);
        const delta = Number((btn as HTMLElement).dataset.step ?? '0');
        let next = (this.memoryPickerCounts[tier] ?? 0) + delta;
        next = Math.max(0, Math.min(owned, next));
        this.memoryPickerCounts[tier] = next;
        this.updateMemoryRow(root, tier);
      });
    });

    root.querySelectorAll('[data-step-max]').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.memory-tier-row') as HTMLElement | null;
        const tier = row?.dataset.tier;
        if (!tier) return;
        this.memoryPickerCounts[tier] = this.getOwned(tier);
        this.updateMemoryRow(root, tier);
      });
    });

    root.querySelectorAll('[data-spend-tier]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const tier = (btn as HTMLElement).dataset.spendTier!;
        const count = this.memoryPickerCounts[tier] ?? 0;
        if (count <= 0) return;
        await this.spendMemory(tier, count);
      });
    });
  }

  private async spendMemory(tier: string, count: number): Promise<void> {
    if (count <= 0 || !this.selectedUnitId) return;
    const root = this.rootEl?.querySelector('.unit-detail-tab-content');
    const allButtons = root?.querySelectorAll('button') ?? [];
    allButtons.forEach(b => (b as HTMLButtonElement).disabled = true);
    try {
      const resp = await APIManager.spendMemory(this.selectedUnitId, tier, count);
      if (resp.success) {
        await this.refreshState();
        // refreshState updates playerData, which fires the playerData
        // subscription → onRender → renderUnitDetail. The new render reads
        // fresh owned counts via getOwned() and skips empty rows entirely.
        const newOwned = this.getOwned(tier);
        this.memoryPickerCounts[tier] = newOwned > 0 ? Math.min(this.memoryPickerCounts[tier] ?? 1, newOwned) : 0;
      } else {
        allButtons.forEach(b => (b as HTMLButtonElement).disabled = false);
      }
    } catch {
      allButtons.forEach(b => (b as HTMLButtonElement).disabled = false);
    }
  }

  private async applySkin(unitId: string, skinId: string | null): Promise<void> {
    try {
      const resp = await APIManager.setUnitSkin(unitId, skinId);
      if (resp.success) {
        await this.refreshState();
      }
    } catch (e) {
      // silent fail
    }
  }

  private async adminLevelUpSelected(): Promise<void> {
    if (!this.selectedUnitId) return;
    const btn = this.$('.party-admin-levelup-btn') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    try {
      const result = await APIManager.adminLevelUpUnit(this.selectedUnitId);
      if (result.success) await this.refreshState();
      else console.error('[PartyScreen] adminLevelUpUnit failed:', result.error);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  // ─── Affix Section (Level 2 detail) ───

  private renderAffixSection(unit: PartyUnit, configData: ConfigData | undefined): string {
    const html = renderAffixesCompact(unit, configData);
    return `
      <div class="unit-detail-affixes">
        <div class="section-label"><span>Traits</span></div>
        ${html || '<div class="affix-empty">No traits.</div>'}
      </div>
    `;
  }

  // ─── Mastery Section ───

  private renderStatsPanel(cs: ComputedStats): string {
    const sections: string[] = [];

    // ── Core ──
    const coreRows: string[] = [];
    coreRows.push(this.statRow('Health', `${cs.hp}`));
    coreRows.push(this.statRow('Speed', `${cs.spd}`));
    if (cs.focus > 0) coreRows.push(this.statRow('Focus', `${cs.focus}`));
    if (cs.focusRegen > 0) coreRows.push(this.statRow('Focus Regen', `${cs.focusRegen}/turn`));

    sections.push(`
      <div class="stats-section">
        <div class="section-label"><span>Core</span></div>
        <div class="stats-rows">${coreRows.join('')}</div>
      </div>
    `);

    // ── Offense ──
    const offenseRows: string[] = [];

    // Global increased damage (from gear intrinsics like staff implicit)
    if (cs.equipIncreasedDamage > 0) {
      offenseRows.push(this.statRow('Increased Damage', `+${cs.equipIncreasedDamage}%`));
    }

    // Tag-conditional increased damage (mastery ranks + gear affixes, already combined in tagIncreased)
    const tagInc = cs.tagIncreased ?? {};
    for (const [tag, val] of Object.entries(tagInc)) {
      if (val > 0) {
        const tagName = formatElement(tag);
        offenseRows.push(this.statRow(`Increased ${tagName} Damage`, `+${val}%`, elementColor(tag)));
      }
    }

    // Mastery flat damage (Bladecraft etc.)
    if (cs.masteryFlatDamage > 0) {
      offenseRows.push(this.statRow('Added Damage', `+${cs.masteryFlatDamage}`));
    }

    // Crit
    if (cs.equipCritChance > 0) {
      offenseRows.push(this.statRow('Critical Strike Chance', `+${cs.equipCritChance}%`));
    }
    if (cs.equipIncreasedCritChance > 0) {
      offenseRows.push(this.statRow('Increased Critical Strike Chance', `+${cs.equipIncreasedCritChance}%`));
    }
    if (cs.equipCritMulti > 0) {
      offenseRows.push(this.statRow('Critical Strike Multiplier', `+${cs.equipCritMulti}%`));
    }

    // Lifesteal
    if (cs.equipLifesteal > 0) {
      offenseRows.push(this.statRow('Lifesteal', `${cs.equipLifesteal}%`));
    }

    // Combat economy / mastery offense
    if (cs.impactDamage > 0) {
      offenseRows.push(this.statRow('Impact Damage', `+${cs.impactDamage}%`));
    }
    if (cs.staggerPotency > 0) {
      offenseRows.push(this.statRow('Stagger Potency', `+${cs.staggerPotency}%`));
    }
    if (cs.fervorPotency > 0) {
      offenseRows.push(this.statRow('Fervor Potency', `+${cs.fervorPotency}%`));
    }
    if (cs.tempoGeneration > 0) {
      offenseRows.push(this.statRow('Tempo Generation', `+${cs.tempoGeneration}%`));
    }

    // Conditional damage
    if (cs.masteryBonusVsDebuffed > 0) {
      offenseRows.push(this.statRow('Damage against Debuffed Enemies', `+${cs.masteryBonusVsDebuffed}%`));
    }
    if (cs.bonusVsFullHp > 0) {
      offenseRows.push(this.statRow('Damage against Full Health Enemies', `+${cs.bonusVsFullHp}%`));
    }
    if (cs.firstStrikeBonus > 0) {
      offenseRows.push(this.statRow('Damage on First Attack', `+${cs.firstStrikeBonus}%`));
    }

    // Combo / chain
    if (cs.extraComboHits > 0) {
      offenseRows.push(this.statRow('Additional Combo Hits', `+${cs.extraComboHits}`));
    }
    if (cs.chainChance > 0) {
      offenseRows.push(this.statRow('Projectile Chain Chance', `${cs.chainChance}%`));
    }

    // Cost reductions
    if (cs.masteryMomentumReduction > 0) {
      offenseRows.push(this.statRow('Reduced Momentum Cost', `${cs.masteryMomentumReduction}%`));
    }
    if (cs.masteryFocusCostReduction > 0) {
      offenseRows.push(this.statRow('Reduced Focus Cost', `${cs.masteryFocusCostReduction}%`));
    }

    // Flat Added Damage
    const flatDmg = cs.flatAddedDamage ?? {};
    for (const [el, range] of Object.entries(flatDmg)) {
      const elName = formatElement(el);
      offenseRows.push(this.statRow(`Adds ${range.min} to ${range.max} ${elName} Damage`, '', elementColor(el)));
    }

    if (offenseRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Offense</span></div>
          <div class="stats-rows">${offenseRows.join('')}</div>
        </div>
      `);
    }

    // ── DoT ──
    const dotRows: string[] = [];
    if (cs.equipDotPotency > 0) dotRows.push(this.statRow('DoT Potency', `+${cs.equipDotPotency}%`));
    if (cs.dotMultiplier > 0) dotRows.push(this.statRow('DoT Multiplier', `+${cs.dotMultiplier}%`));
    if (cs.flatDotDamage > 0) dotRows.push(this.statRow('Added DoT Damage', `+${cs.flatDotDamage}`));
    if (cs.burnDotPotency > 0) dotRows.push(this.statRow('Burn DoT Potency', `+${cs.burnDotPotency}%`, elementColor('fire')));
    if (cs.bleedDotPotency > 0) dotRows.push(this.statRow('Bleed DoT Potency', `+${cs.bleedDotPotency}%`, elementColor('steel')));
    if (cs.poisonDotPotency > 0) dotRows.push(this.statRow('Poison DoT Potency', `+${cs.poisonDotPotency}%`, elementColor('nature')));
    if (cs.decayDotPotency > 0) dotRows.push(this.statRow('Decay DoT Potency', `+${cs.decayDotPotency}%`, elementColor('dark')));

    if (dotRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Damage Over Time</span></div>
          <div class="stats-rows">${dotRows.join('')}</div>
        </div>
      `);
    }

    // ── Defense ──
    const defenseRows: string[] = [];
    if (cs.guard > 0) defenseRows.push(this.statRow('Guard', `${cs.guard}`));
    if (cs.guardPercent > 0) defenseRows.push(this.statRow('Increased Guard', `+${cs.guardPercent}%`));
    if (cs.shieldOnTurnStart > 0) defenseRows.push(this.statRow('Barrier at Turn Start', `${cs.shieldOnTurnStart}% of Max Health`));
    if (cs.critAvoidance > 0) defenseRows.push(this.statRow('Critical Strike Avoidance', `${cs.critAvoidance}%`));
    if (cs.dotDamageTaken > 0) defenseRows.push(this.statRow('Reduced Damage over Time Taken', `${cs.dotDamageTaken}%`));
    if (cs.statusResistance > 0) defenseRows.push(this.statRow('Reduced Debuff Duration', `${cs.statusResistance}%`));

    if (defenseRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Defense</span></div>
          <div class="stats-rows">${defenseRows.join('')}</div>
        </div>
      `);
    }

    // ── Barriers ──
    const barrierRows: string[] = [];
    if (cs.barrierOnHit > 0) barrierRows.push(this.statRow('Barrier On Hit', `${cs.barrierOnHit}`));
    if (cs.barrierOnCrit > 0) barrierRows.push(this.statRow('Barrier On Critical Strike', `${cs.barrierOnCrit}`));
    if (cs.barrierDuration > 0) barrierRows.push(this.statRow('Barrier Duration', `+${cs.barrierDuration} turns`));
    if (cs.masteryBarrierPotency > 0) barrierRows.push(this.statRow('Barrier Potency', `+${cs.masteryBarrierPotency}%`));
    if (cs.barrierActiveRes > 0) barrierRows.push(this.statRow('Resistance while Barrier Active', `+${cs.barrierActiveRes}%`));
    if (cs.barrierActiveDr > 0) barrierRows.push(this.statRow('Reduced Damage Taken while Barrier Active', `${cs.barrierActiveDr}%`));
    if (cs.barrierActiveDamage > 0) barrierRows.push(this.statRow('Damage while Barrier Active', `+${cs.barrierActiveDamage}%`));

    if (barrierRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Barriers</span></div>
          <div class="stats-rows">${barrierRows.join('')}</div>
        </div>
      `);
    }

    // ── Reactive (when struck / conditional) ──
    const reactiveRows: string[] = [];
    if (cs.hpOnStruck > 0) reactiveRows.push(this.statRow('Health When Struck', `+${cs.hpOnStruck}`));
    if (cs.focusOnStruckArmor > 0) reactiveRows.push(this.statRow('Focus When Struck', `+${cs.focusOnStruckArmor}`));
    if (cs.thornsDamage > 0) reactiveRows.push(this.statRow('Thorns Damage', `${cs.thornsDamage}`));
    if (cs.momentumOnStruck > 0) reactiveRows.push(this.statRow('Momentum When Struck', `+${Math.round(cs.momentumOnStruck / 100)}`));
    if (cs.corneredSpd > 0) reactiveRows.push(this.statRow('Speed while below 50% Health', `+${cs.corneredSpd}`));
    if (cs.speedOnKill > 0) reactiveRows.push(this.statRow('Speed On Kill (2 turns)', `+${cs.speedOnKill}`));

    if (reactiveRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Reactive</span></div>
          <div class="stats-rows">${reactiveRows.join('')}</div>
        </div>
      `);
    }

    // ── Recovery ──
    const recoveryRows: string[] = [];
    if (cs.hpRegen > 0) recoveryRows.push(this.statRow('Health Regen', `+${cs.hpRegen}/turn`));
    if (cs.lifeOnHit > 0) recoveryRows.push(this.statRow('Health On Hit', `+${cs.lifeOnHit}`));
    if (cs.healOnKill > 0) recoveryRows.push(this.statRow('Health On Kill', `${cs.healOnKill}% of Max Health`));
    if (cs.healOnCombatEnd > 0) recoveryRows.push(this.statRow('Health at Combat End', `${cs.healOnCombatEnd}% of Max Health`));
    if (cs.focusOnCrit > 0) recoveryRows.push(this.statRow('Focus On Critical Strike', `+${cs.focusOnCrit}`));
    if (cs.focusOnKill > 0) recoveryRows.push(this.statRow('Focus On Kill', `+${cs.focusOnKill}`));
    if (cs.focusOnHit > 0) recoveryRows.push(this.statRow('Focus On Hit', `+${cs.focusOnHit}`));
    if (cs.healingDone > 0) recoveryRows.push(this.statRow('Healing Done', `+${cs.healingDone}%`));
    if (cs.healingReceived > 0) recoveryRows.push(this.statRow('Healing Received', `+${cs.healingReceived}%`));

    if (recoveryRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Recovery</span></div>
          <div class="stats-rows">${recoveryRows.join('')}</div>
        </div>
      `);
    }

    // ── Buffs & Debuffs ──
    const auraRows: string[] = [];
    if (cs.boonPotency > 0) auraRows.push(this.statRow('Boon Potency', `+${cs.boonPotency}%`));
    if (cs.buffDuration > 0) auraRows.push(this.statRow('Buff Duration', `+${cs.buffDuration}%`));
    if (cs.buffDurationFlat > 0) auraRows.push(this.statRow('Buff Duration', `+${cs.buffDurationFlat} turns`));
    if (cs.hexPotency > 0) auraRows.push(this.statRow('Hex Potency', `+${cs.hexPotency}%`));
    if (cs.ruinPotency > 0) auraRows.push(this.statRow('Ruin Potency', `+${cs.ruinPotency}%`));
    if (cs.debuffDuration > 0) auraRows.push(this.statRow('Debuff Duration', `+${cs.debuffDuration}%`));
    if (cs.debuffDurationFlat > 0) auraRows.push(this.statRow('Debuff Duration', `+${cs.debuffDurationFlat} turns`));
    if (cs.chanceInflictExposed > 0) auraRows.push(this.statRow('Chance to Inflict Exposed On Hit', `${cs.chanceInflictExposed}%`));

    if (auraRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Buffs &amp; Debuffs</span></div>
          <div class="stats-rows">${auraRows.join('')}</div>
        </div>
      `);
    }

    // ── Minions ──
    const minionRows: string[] = [];
    if (cs.minionDamageIncreased > 0) minionRows.push(this.statRow('Minion Damage', `+${cs.minionDamageIncreased}%`));
    if (cs.minionHealthIncreased > 0) minionRows.push(this.statRow('Minion Maximum Health', `+${cs.minionHealthIncreased}%`));
    if (cs.minionSpd > 0) minionRows.push(this.statRow('Minion Speed', `+${cs.minionSpd}`));
    if (cs.minionCritChance > 0) minionRows.push(this.statRow('Minion Critical Strike Chance', `+${cs.minionCritChance}%`));
    if (cs.minionCritMulti > 0) minionRows.push(this.statRow('Minion Critical Strike Multiplier', `+${cs.minionCritMulti}%`));

    if (minionRows.length > 0) {
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Minions</span></div>
          <div class="stats-rows">${minionRows.join('')}</div>
        </div>
      `);
    }

    // ── Penetration ──
    const pen = cs.penetration ?? {};
    const penEntries = Object.entries(pen).filter(([, v]) => v > 0);
    if (penEntries.length > 0) {
      const penRows = penEntries.map(([group, val]) => {
        const label = formatElement(group);
        return this.statRow(`${label} Pen`, `${val}`);
      }).join('');
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Penetration</span></div>
          <div class="stats-rows">${penRows}</div>
        </div>
      `);
    }

    // ── Resistances ──
    const profile = cs.resistanceProfile;
    if (profile) {
      // Map each element to its gear resistance group key
      const ELEMENT_GROUP: Record<string, string> = {
        steel: 'steel', brute: 'brute', finesse: 'finesse',
        fire: 'elemental', water: 'elemental', lightning: 'elemental',
        nature: 'primal', wind: 'primal', earth: 'primal',
        holy: 'cosmic', dark: 'cosmic', astral: 'cosmic',
      };

      const RES_GROUPS: { label: string; elements: { key: string; name: string }[] }[] = [
        { label: 'Physical', elements: [
          { key: 'steel', name: 'Steel' },
          { key: 'brute', name: 'Brute' },
          { key: 'finesse', name: 'Finesse' },
        ]},
        { label: 'Elemental', elements: [
          { key: 'fire', name: 'Fire' },
          { key: 'water', name: 'Water' },
          { key: 'lightning', name: 'Lightning' },
        ]},
        { label: 'Primal', elements: [
          { key: 'nature', name: 'Nature' },
          { key: 'wind', name: 'Wind' },
          { key: 'earth', name: 'Earth' },
        ]},
        { label: 'Cosmic', elements: [
          { key: 'holy', name: 'Holy' },
          { key: 'dark', name: 'Dark' },
          { key: 'astral', name: 'Astral' },
        ]},
      ];

      const gearRes = cs.gearResistance ?? {};

      const resHtml = RES_GROUPS.map(g => {
        const rows = g.elements.map(el => {
          const val = profile[el.key] ?? 0;
          const group = ELEMENT_GROUP[el.key] ?? '';
          const gear = group ? (gearRes[group] ?? 0) : 0;
          const base = val - gear;
          const sign = val > 0 ? '+' : '';
          const cls = val > 0 ? 'res-positive' : val < 0 ? 'res-negative' : 'res-neutral';
          return `<div class="res-row" data-res-el="${el.key}" data-res-base="${base}" data-res-gear="${gear}" data-res-total="${val}"><span class="res-el">${el.name}</span><span class="res-val ${cls}">${sign}${val}</span></div>`;
        }).join('');
        return `<div class="res-group"><span class="res-group-label">${g.label}</span>${rows}</div>`;
      }).join('');

      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Resistances</span></div>
          <div class="res-groups">${resHtml}</div>
        </div>
      `);
    }

    // ── Skill Damage Estimates ──
    const estimates = cs.skillEstimates ?? [];
    if (estimates.length > 0) {
      const skillRows = estimates.map(g => this.renderSkillEstimate(g)).join('');
      sections.push(`
        <div class="stats-section">
          <div class="section-label"><span>Skill Damage</span></div>
          <div class="skill-estimates">${skillRows}</div>
        </div>
      `);
    }

    return `<div class="unit-detail-stats">${sections.join('')}</div>`;
  }

  private statRow(label: string, value: string, color?: string): string {
    const style = color ? ` style="color:${color}"` : '';
    return `<div class="stat-row"><span class="stat-row-label">${label}</span><span class="stat-row-value"${style}>${value}</span></div>`;
  }

  private renderSkillEstimate(g: SkillEstimate): string {
    const elColor = elementColor(g.element);
    const elName = formatElement(g.element);
    const isHeal = g.type === 'heal';
    const rangeLabel = isHeal ? 'Heal' : elName;
    const rangeColor = isHeal ? 'var(--color-success)' : elColor;

    let meta = '';
    if (!isHeal) {
      const critParts: string[] = [];
      if (g.critChance > 0) critParts.push(`${Math.round(g.critChance)}% crit`);
      if (g.critMulti > 150) critParts.push(`${Math.round(g.critMulti)}% multi`);
      if (critParts.length > 0) meta = critParts.join(' / ');
    }
    if (g.lifesteal > 0) {
      meta += (meta ? ' · ' : '') + `${g.lifesteal}% ls`;
    }

    return `
      <div class="skill-est">
        <div class="skill-est-header">
          <span class="skill-est-name" style="color:${elColor}">${g.name}</span>
        </div>
        <div class="skill-est-range">
          <span style="color:${rangeColor}">${g.minDamage} - ${g.maxDamage} ${rangeLabel}</span>
          ${g.increasedPercent > 0 ? `<span class="skill-est-inc">(+${Math.round(g.increasedPercent)}%)</span>` : ''}
        </div>
        ${meta ? `<div class="skill-est-meta">${meta}</div>` : ''}
      </div>
    `;
  }

  private renderXpBar(unit: PartyUnit): string {
    const progressXp = unit.xpIntoLevel ?? 0;
    const levelXpRange = unit.xpToNextLevel ?? 0;
    if (levelXpRange <= 0) {
      return '<div class="unit-xp-bar-wrap"><span class="unit-xp-label">MAX LEVEL</span></div>';
    }
    const pct = Math.min(100, Math.max(0, (progressXp / levelXpRange) * 100));
    return `
      <div class="unit-xp-bar-wrap">
        <div class="unit-xp-bar"><div class="unit-xp-fill" style="width:${pct}%"></div></div>
        <span class="unit-xp-label">${progressXp}/${levelXpRange} XP</span>
      </div>
    `;
  }

  /** Build tooltip HTML for a mastery row */
  private buildMasteryTooltip(def: MasteryDef, rank: number, base: number, bonus: number, tier: number, rankCap: number, nextCost: number): string {
    const tierLabels = ['Prodigy', 'Core', 'Affinity', 'Strong', 'Moderate', 'Distant', 'Foreign', 'Hostile'];
    const tierColors = ['#ffb878', '#88e08c', '#7ed8c8', '#88b8e8', '#cccc66', '#ffaa44', '#cc88dd', '#ff5555'];
    const tierIdx = Math.max(0, Math.min(7, tier - 1));
    const affinityLabel = tierLabels[tierIdx];
    const affinityColor = tierColors[tierIdx];
    const lines: string[] = [];
    lines.push(`<div class="tooltip-inner">`);
    const elName = def.element ? def.element.charAt(0).toUpperCase() + def.element.slice(1) : '';
    const elPill = def.element
      ? `<span class="tooltip-element" style="color:${elementColor(def.element)};">${elName}</span>`
      : '';
    // Mastery rank title — Novice / Apprentice / Adept / Master based on rank band.
    const title = rank <= 0 ? null : rank <= 25 ? 'Novice' : rank <= 50 ? 'Apprentice' : rank <= 75 ? 'Adept' : 'Master';
    const titleSuffix = title ? ` <span class="label">(${title})</span>` : '';
    lines.push(`<div class="tooltip-header"><span class="tooltip-name-group">${renderMasteryIcon(def.id)}<span class="tooltip-name">${def.name}</span></span>${elPill}</div>`);
    lines.push(`<div class="tooltip-line">${def.description}</div>`);
    lines.push(`<div class="tooltip-separator"></div>`);
    lines.push(`<div class="tooltip-line">Rank: <span class="value">${base}</span> / ${rankCap} <span class="label">(strand Lv)</span>${bonus > 0 ? ` <span class="value heal">(+${bonus} gear)</span>` : ''}${titleSuffix}</div>`);
    // Show rank bonus from mastery
    const rb = def.rankBonus;
    if (rb) {
      if (rank > 0) {
        const val = rb.perRank * rank;
        const formatted = Number.isInteger(val) ? String(val) : val.toFixed(1);
        const unitAndLabel = rb.label.replace(/^[+-]?[\d.]+/, '');
        const sign = rb.label.startsWith('-') ? '-' : '+';
        lines.push(`<div class="tooltip-line">Bonus: <span class="value heal">${sign}${formatted}${unitAndLabel}</span></div>`);
      } else {
        lines.push(`<div class="tooltip-line">Per rank: <span class="label">${rb.label}</span></div>`);
      }
    }
    if (base < rankCap) {
      lines.push(`<div class="tooltip-line">Next rank: <span class="value">${nextCost} pts</span></div>`);
    } else {
      lines.push(`<div class="tooltip-line"><span class="label">Reach Lv ${base + 1} to invest further.</span></div>`);
    }
    lines.push(`<div class="tooltip-line">Fluency: <span style="color:${affinityColor};font-weight:600;">${affinityLabel}</span></div>`);
    lines.push(`</div>`);
    return lines.join('');
  }

  /** Cost to purchase a specific rank given a cost tier. Mirrors backend masteryRankCost. */
  private rankCost(rank: number, tier: number): number {
    if (rank < 1) return 0;
    const band: 1 | 2 | 3 | 4 = rank <= 25 ? 1 : rank <= 50 ? 2 : rank <= 75 ? 3 : 4;
    if (tier === 1) return [1, 1, 3, 4][band - 1]; // Prodigy bespoke ramp
    // band1Cost lookup — Hostile (tier 8) jumps off the linear ramp.
    const band1 = ({ 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 7: 6, 8: 8 } as Record<number, number>)[tier] ?? 8;
    return band1 * band;
  }

  /** Total cost to go from rank `from` to rank `to` (band-aware, per-rank ramp). */
  private totalCostForRanks(from: number, to: number, tier: number): number {
    if (to <= from) return 0;
    let total = 0;
    for (let r = from + 1; r <= to; r++) total += this.rankCost(r, tier);
    return total;
  }

  private renderMasterySection(unit: PartyUnit, cs: ComputedStats): string {
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const masteries = configData?.masteries;
    if (!masteries) return '';
    // Mastery rank caps at strand level. Mirrors masteryService server-side check
    // so the UI disables the +button before the player can submit a request the
    // backend will reject.
    const rankCap = unit.level ?? 1;

    const costs: Record<string, number> = cs.effectiveMasteryCosts ?? (unit.masteryCosts ? JSON.parse(unit.masteryCosts) : {});
    const baseRanks: Record<string, number> = unit.masteryRanks ? JSON.parse(unit.masteryRanks) : {};
    const effectiveRanks = cs.masteryRanks || {};

    // Pending ranks (not points) — each +1 click queues one rank
    const totalPendingRanks = Object.values(this.pendingMasteryPoints).reduce((s, v) => s + v, 0);
    // Total cost of all pending ranks
    let totalPendingCost = 0;
    for (const [id, pendingRanks] of Object.entries(this.pendingMasteryPoints)) {
      if (pendingRanks <= 0) continue;
      const tier = costs[id] ?? 8;
      const base = baseRanks[id] || 0;
      totalPendingCost += this.totalCostForRanks(base, base + pendingRanks, tier);
    }
    const availablePoints = (unit.unspentMasteryPoints ?? 0) - totalPendingCost;

    // Group masteries by category
    const categoryLabels: Record<MasteryCategory, string> = {
      primal: 'Primal Elements',
      cosmic: 'Cosmic Arts',
      martial: 'Martial Disciplines',
      cunning: 'Cunning Tactics',
    };
    const categoryOrder: MasteryCategory[] = ['primal', 'cosmic', 'martial', 'cunning'];
    const groups = new Map<string, { label: string; entries: [string, MasteryDef][] }>();
    for (const cat of categoryOrder) {
      groups.set(cat, { label: categoryLabels[cat], entries: [] });
    }
    for (const [id, def] of Object.entries(masteries) as [string, MasteryDef][]) {
      const cat = (def as MasteryDef).category ?? 'martial';
      if (!groups.has(cat)) groups.set(cat, { label: cat, entries: [] });
      groups.get(cat)!.entries.push([id, def]);
    }

    // Determine filter state
    // Recommended view shows tiers 1-4 (Prodigy through Strong) — the "accessible" zone.
    // Anything tier 5+ (Moderate and worse) gets hidden unless the player invested in it.
    const RECOMMENDED_THRESHOLD = 4;
    const filterActive = this.masteryFilterRecommended;
    const hasCosts = Object.keys(costs).length > 0;

    let html = `<div class="mastery-section">`;

    // Header row: points + filter toggle
    html += `<div class="mastery-section-header">`;
    if ((unit.unspentMasteryPoints ?? 0) > 0) {
      html += `<span class="mastery-points-inline"><span class="mastery-points-value">${availablePoints}</span> points${totalPendingRanks > 0 ? ` <span class="mastery-pending-count">(${totalPendingCost} queued)</span>` : ''}</span>`;
    } else {
      html += `<span></span>`;
    }
    if (hasCosts) {
      html += `<button class="mastery-filter-toggle${filterActive ? ' active' : ''}">${filterActive ? 'Recommended' : 'All'}</button>`;
    }
    html += `</div>`;

    for (const [, group] of groups) {
      let groupHtml = '';
      let visibleCount = 0;

      for (const [id, def] of group.entries) {
        const isNyi = (def as MasteryDef).status === 'nyi';
        const pending = this.pendingMasteryPoints[id] || 0;
        const tier = costs[id] ?? 8;

        // Filter logic — when Recommended is active, hide off-class masteries
        // UNLESS the player has invested or queued ranks in them (so existing
        // investments stay visible and tunable without forcing All mode).
        if (filterActive && hasCosts) {
          const baseRank = baseRanks[id] || 0;
          if (isNyi && pending <= 0) continue;
          if (!isNyi && tier > RECOMMENDED_THRESHOLD && pending <= 0 && baseRank <= 0) continue;
        }

        if (isNyi) {
          groupHtml += `
            <div class="mastery-row mastery-row--nyi">
              <span class="mastery-icon">${renderMasteryIcon(id, 'md')}</span>
              <div class="mastery-info">
                <span class="mastery-name">${def.name} <span class="mastery-nyi-badge">NYI</span></span>
              </div>
              <span class="mastery-rank"></span>
              <span class="mastery-btn-cell"></span>
            </div>`;
          visibleCount++;
          continue;
        }

        const rank = effectiveRanks[id] || 0;
        const base = baseRanks[id] || 0;
        const bonus = rank - base;
        const nextRank = base + pending + 1;
        const nextCost = nextRank <= rankCap ? this.rankCost(nextRank, tier) : Infinity;
        const canAdd = availablePoints >= nextCost && (base + pending) < rankCap;

        const pendingStr = pending > 0 ? `<span class="mastery-pending-preview">\u2192${base + pending}</span>` : '';

        // Inline rank bonus (e.g. "+6% DoT Potency") — always shown so players see the value
        let rankBonusHtml = '';
        const rb = (def as MasteryDef).rankBonus;
        if (rb) {
          const effectiveRank = rank + pending;
          if (effectiveRank > 0) {
            const val = rb.perRank * effectiveRank;
            const formatted = Number.isInteger(val) ? String(val) : val.toFixed(1);
            // Replace the first number in the label (with optional +/- sign) so labels
            // like "Regenerate 0.5 Health per turn" scale correctly, not just "+2% …" forms.
            const scaledLabel = rb.label.replace(/([+-]?)[\d.]+/, (_, sign) => `${sign}${formatted}`);
            rankBonusHtml = `<span class="mastery-rank-bonus">${scaledLabel}</span>`;
          } else {
            // Show what rank 1 would give in dim text — the label is already authored at the perRank value
            rankBonusHtml = `<span class="mastery-rank-bonus mastery-rank-bonus--preview">${rb.label}</span>`;
          }
        }

        // Cost tier color class for invest button. tier 1-2 = core (cheap), 3-4 = strong, 5-6 = moderate, 7+ = offclass.
        const costTier = tier <= 2 ? 'cost-core' : tier <= 4 ? 'cost-strong' : tier <= 6 ? 'cost-moderate' : 'cost-offclass';

        // Color-code the rank number by mastery title — Novice / Apprentice / Adept / Master,
        // aligned with the cost bands. The actual title word lives in the tooltip; the rank-number
        // color is the at-a-glance signal.
        const titleKey = rank <= 0
          ? null
          : rank <= 25 ? 'novice' : rank <= 50 ? 'apprentice' : rank <= 75 ? 'adept' : 'master';
        const titleClass = titleKey ? ` mastery-rank--${titleKey}` : '';

        groupHtml += `
            <div class="mastery-row${pending > 0 ? ' has-pending' : ''}">
              <span class="mastery-icon">${renderMasteryIcon(id, 'md')}</span>
              <div class="mastery-info" data-mastery-tip="${id}">
                <span class="mastery-name">${def.name}</span>
                ${rankBonusHtml}
              </div>
              <span class="mastery-rank${rank === 0 && pending === 0 ? ' mastery-rank--zero' : ''}${titleClass}">${rank}${pendingStr}</span>
              <span class="mastery-btn-cell">${(unit.unspentMasteryPoints ?? 0) > 0 ? `<button class="mastery-invest-btn ${costTier}${canAdd ? '' : ' disabled'}" data-mastery="${id}"${canAdd ? '' : ' disabled'}>${(base + pending) >= rankCap ? `Lv ${(base + pending) + 1}` : nextCost}</button>` : ''}</span>
            </div>`;
        visibleCount++;
      }

      if (visibleCount === 0) continue;

      html += `<div class="mastery-group">`;
      html += `<div class="section-label mastery-group-header">`;
      html += `<span>${group.label}</span>`;
      html += `</div>`;
      html += `<div class="mastery-group-body">`;
      html += groupHtml;
      html += `</div></div>`;
    }

    html += `</div>`;
    return html;
  }

  private renderPendingBar(unit: PartyUnit): string {
    const totalPendingRanks = Object.values(this.pendingMasteryPoints).reduce((s, v) => s + v, 0);
    if (totalPendingRanks === 0) return '';

    const configData = StateManager.get('configData') as ConfigData | undefined;
    const masteries = configData?.masteries;
    if (!masteries) return '';

    const cs = unit.computedStats as ComputedStats | undefined;
    const costs: Record<string, number> = cs?.effectiveMasteryCosts ?? (unit.masteryCosts ? JSON.parse(unit.masteryCosts) : {});
    const baseRanks: Record<string, number> = unit.masteryRanks ? JSON.parse(unit.masteryRanks) : {};

    let totalPendingCost = 0;
    for (const [id, pendingRanks] of Object.entries(this.pendingMasteryPoints)) {
      if (pendingRanks <= 0) continue;
      const tier = costs[id] ?? 8;
      const base = baseRanks[id] || 0;
      totalPendingCost += this.totalCostForRanks(base, base + pendingRanks, tier);
    }

    return `
      <div class="mastery-pending-bar">
        <div class="mastery-pending-actions">
          <button class="mastery-cancel-btn" type="button">Cancel</button>
          <button class="mastery-confirm-btn" type="button">Confirm (${totalPendingCost} pts)</button>
        </div>
      </div>`;
  }

  private bindResistanceTooltips(): void {
    this.rootEl?.querySelectorAll('.res-row[data-res-el]').forEach(row => {
      const el = row as HTMLElement;
      Tooltip.attach(el, () => {
        const name = el.dataset.resEl ?? '';
        const base = parseInt(el.dataset.resBase ?? '0', 10);
        const gear = parseInt(el.dataset.resGear ?? '0', 10);
        const total = parseInt(el.dataset.resTotal ?? '0', 10);
        const label = formatElement(name);
        const mult = Math.max(0.25, Math.min(1.75, 1 - total / 100));
        const multPct = Math.round(mult * 100);
        const multCls = mult < 1 ? 'res-positive' : mult > 1 ? 'res-negative' : 'res-neutral';

        const lines: string[] = [];
        lines.push(`<div class="tooltip-inner">`);
        lines.push(`<div class="tooltip-header"><span class="tooltip-name">${label} Resistance</span></div>`);
        lines.push(`<div class="tooltip-separator"></div>`);
        if (base !== 0) {
          const baseSign = base > 0 ? '+' : '';
          lines.push(`<div class="tooltip-line" style="display:flex;justify-content:space-between"><span class="label">Type chart</span><span class="value">${baseSign}${base}</span></div>`);
        }
        if (gear !== 0) {
          const gearSign = gear > 0 ? '+' : '';
          lines.push(`<div class="tooltip-line" style="display:flex;justify-content:space-between"><span class="label">Gear</span><span class="value">${gearSign}${gear}</span></div>`);
        }
        if (base === 0 && gear === 0) {
          lines.push(`<div class="tooltip-line"><span class="label">No modifiers</span></div>`);
        }
        lines.push(`<div class="tooltip-separator"></div>`);
        lines.push(`<div class="tooltip-line" style="display:flex;justify-content:space-between"><span>Damage taken</span><span class="${multCls}" style="font-weight:600">${multPct}%</span></div>`);
        lines.push(`</div>`);
        return lines.join('');
      });
    });
  }

  /** Replace only the mastery section + pending bar in-place (no full detail rebuild). */
  private refreshMasteryInPlace(): void {
    const detailEl = this.$('.unit-detail');
    if (!detailEl) return;
    const unit = this.getSelectedUnit();
    if (!unit) return;
    const cs = unit.computedStats;

    // Replace mastery section
    const oldSection = detailEl.querySelector('.mastery-section');
    if (oldSection) {
      const temp = document.createElement('div');
      temp.innerHTML = this.renderMasterySection(unit, cs);
      const newSection = temp.firstElementChild;
      if (newSection) oldSection.replaceWith(newSection);
    }

    // Replace or add/remove pending bar
    const oldBar = detailEl.querySelector('.mastery-pending-bar');
    const newBarHtml = this.renderPendingBar(unit);
    if (oldBar) {
      if (newBarHtml) {
        const temp = document.createElement('div');
        temp.innerHTML = newBarHtml;
        oldBar.replaceWith(temp.firstElementChild!);
      } else {
        oldBar.remove();
      }
    } else if (newBarHtml) {
      detailEl.insertAdjacentHTML('beforeend', newBarHtml);
    }

    // Rebind mastery-specific listeners
    this.bindMasteryButtons();
  }

  private bindMasteryButtons(): void {
    const unit = this.getSelectedUnit();
    if (!unit) return;

    const configData = StateManager.get('configData') as ConfigData | undefined;
    const masteries = configData?.masteries;
    const cs = unit.computedStats as ComputedStats | undefined;
    const rankCap = unit.level ?? 1;

    // Tooltips for each mastery row
    if (masteries && cs) {
      const costs: Record<string, number> = cs.effectiveMasteryCosts ?? (unit.masteryCosts ? JSON.parse(unit.masteryCosts) : {});
      const baseRanks: Record<string, number> = unit.masteryRanks ? JSON.parse(unit.masteryRanks) : {};
      const effectiveRanks = cs.masteryRanks || {};

      this.rootEl?.querySelectorAll('[data-mastery-tip]').forEach(row => {
        const id = (row as HTMLElement).dataset.masteryTip!;
        const def = masteries[id] as MasteryDef | undefined;
        if (!def) return;
        const base = baseRanks[id] || 0;
        const rank = effectiveRanks[id] || 0;
        const bonus = rank - base;
        const tier = costs[id] ?? 8;
        const pending = this.pendingMasteryPoints[id] || 0;
        const nextRank = base + pending + 1;
        const nextCost = nextRank <= rankCap ? this.rankCost(nextRank, tier) : Infinity;
        Tooltip.attach(row as HTMLElement, () => this.buildMasteryTooltip(def, rank, base, bonus, tier, rankCap, nextCost));
      });
    }

    // Filter toggle
    this.rootEl?.querySelector('.mastery-filter-toggle')?.addEventListener('click', () => {
      this.masteryFilterRecommended = !this.masteryFilterRecommended;
      localStorage.setItem('mastery_filter_recommended', String(this.masteryFilterRecommended));
      this.refreshMasteryInPlace();
    });

    // +1 buttons → queue locally
    this.rootEl?.querySelectorAll('.mastery-invest-btn:not(.disabled)').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const masteryId = (e.currentTarget as HTMLElement).dataset.mastery;
        if (!masteryId) return;
        this.pendingMasteryPoints[masteryId] = (this.pendingMasteryPoints[masteryId] || 0) + 1;
        this.refreshMasteryInPlace();
      });
    });

    // Confirm → batch invest (full re-render needed since server data changes)
    this.rootEl?.querySelector('.mastery-confirm-btn')?.addEventListener('click', async () => {
      const investments = Object.entries(this.pendingMasteryPoints)
        .filter(([, r]) => r > 0)
        .map(([masteryId, ranks]) => ({ masteryId, ranks }));
      if (investments.length === 0) return;

      const before = new Map((unit.unlockedSkills ?? []).map(s => [s.id, { name: s.name, rank: s.rank }]));

      const result = await APIManager.investMasteryBatch(unit.id, investments);
      if (result.success) {
        this.pendingMasteryPoints = {};
        await this.refreshState();

        const updated = this.getSelectedUnit();
        const after = new Map((updated?.unlockedSkills ?? []).map(s => [s.id, { name: s.name, rank: s.rank }]));
        let skillEvents = 0;
        for (const [id, post] of after) {
          const prev = before.get(id);
          if (!prev) {
            EventBus.emit(Events.SHOW_TOAST, { type: 'success', message: `Skill unlocked: ${post.name}` });
            skillEvents++;
          } else if (post.rank > prev.rank) {
            EventBus.emit(Events.SHOW_TOAST, { type: 'success', message: `${post.name} ranked up to R${post.rank}` });
            skillEvents++;
          }
        }
        if (skillEvents === 0) {
          EventBus.emit(Events.SHOW_TOAST, { type: 'success', message: 'Mastery points invested!' });
        }
      } else {
        EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Failed to invest mastery points' });
      }
      this.renderUnitDetail();
    });

    // Cancel → clear pending
    this.rootEl?.querySelector('.mastery-cancel-btn')?.addEventListener('click', () => {
      this.pendingMasteryPoints = {};
      this.refreshMasteryInPlace();
    });
  }

  // ─── Skill Slots ───

  /** Resolve which rank a skill is at given mastery ranks */
  private resolveSkillRank(skill: SkillDef, masteryRanks: Record<string, number>): number {
    for (let i = skill.ranks.length - 1; i >= 0; i--) {
      const reqs = skill.ranks[i].masteryRequirements;
      if (!reqs || reqs.length === 0) return i + 1;
      const met = reqs.every(r => (masteryRanks[r.masteryId] ?? 0) >= r.rank);
      if (met) return i + 1;
    }
    return 0;
  }

  private renderSkillSlots(): void {
    const slotsEl = this.$('.skill-slots');
    if (!slotsEl) return;

    const unit = this.getSelectedUnit();
    const configData = StateManager.get('configData') as ConfigData | undefined;
    if (!unit || !configData?.skillDefs || !configData?.abilities) {
      slotsEl.innerHTML = '';
      return;
    }

    const skillIds = SKILL_SLOT_KEYS.map(key => unit[key]);
    // Use backend-computed unlocked skills for rank info (includes gear/trait bonuses)
    const unlockedMap = new Map((unit.unlockedSkills ?? []).map(s => [s.id, s]));

    const cards = skillIds.map((id, i) => {
      const primed = this.primedSkillSlot === i;
      if (!id) {
        return `<div class="skill-slot-card empty${primed ? ' primed' : ''}" data-skill-slot="${i}">
          <div class="skill-slot-empty">Empty</div>
        </div>`;
      }
      const skill = configData.skillDefs![id];
      const ability = configData.abilities[id];
      if (!skill || !ability) {
        return `<div class="skill-slot-card empty${primed ? ' primed' : ''}" data-skill-slot="${i}">
          <div class="skill-slot-empty">Empty</div>
        </div>`;
      }
      const rank = unlockedMap.get(id)?.rank ?? 1;
      const iconSrc = getAbilityIconUrl(id);
      const elCls = `element-${ability.element}`;
      const elColor = elementColor(ability.element);
      return `<div class="skill-slot-card filled${primed ? ' primed' : ''}" data-skill-slot="${i}" data-skill-id="${id}" style="border-left-color:${elColor}">
        <img class="skill-slot-icon" src="${iconSrc}" alt="" width="20" height="20" draggable="false" onerror="this.style.display='none'">
        <span class="skill-slot-name">${skill.name}</span>
        <span class="skill-slot-rank">R${rank}</span>
      </div>`;
    }).join('');

    slotsEl.innerHTML = cards;

    // Attach tooltips to filled skill slots
    slotsEl.querySelectorAll('.skill-slot-card.filled').forEach(el => {
      const id = (el as HTMLElement).dataset.skillId;
      if (id && configData?.abilities[id]) {
        const ability = configData.abilities[id];
        const skillDef = configData.skillDefs?.[id];
        const rank = unlockedMap.get(id)?.rank ?? 1;
        const rankData = skillDef?.ranks[rank - 1];
        Tooltip.attach(el as HTMLElement, () => {
          const elColor = elementColor(ability.element);
          const lines = buildSkillAbilityLines(ability, rankData, configData, {
            unit,
            skillId: id,
            rankCount: skillDef?.ranks.length,
          });
          return `<div class="tooltip-inner">
            <span class="tooltip-name" style="color:${elColor}">${skillDef?.name ?? ability.name}</span>
            ${lines.join('')}
          </div>`;
        }, { delay: 500 });
      }
    });

    // Click slot → open skill browser (replaces gear view)
    slotsEl.querySelectorAll('.skill-slot-card').forEach(el => {
      el.addEventListener('click', () => {
        const slot = parseInt((el as HTMLElement).dataset.skillSlot ?? '0');
        if (this.primedSkillSlot === slot) {
          this.closeSkillBrowser();
        } else {
          this.primedSkillSlot = slot;
          this.renderSkillSlots();
          this.openSkillBrowser();
        }
      });
    });
  }

  private _escHandler: ((e: KeyboardEvent) => void) | null = null;

  private openSkillBrowser(): void {
    const gearView = this.$('.gear-view');
    const browser = this.$('.skill-browser');
    if (!gearView || !browser) return;
    gearView.classList.add('hidden');
    browser.classList.remove('hidden');
    this.renderSkillBrowser();

    // Esc to close
    this._escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeSkillBrowser();
    };
    document.addEventListener('keydown', this._escHandler);
  }

  private closeSkillBrowser(): void {
    const gearView = this.$('.gear-view');
    const browser = this.$('.skill-browser');
    if (!gearView || !browser) return;
    browser.classList.add('hidden');
    gearView.classList.remove('hidden');
    this.primedSkillSlot = null;
    this.renderSkillSlots();

    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
  }

  private renderSkillBrowser(): void {
    const browser = this.$('.skill-browser');
    if (!browser) return;

    const unit = this.getSelectedUnit();
    const config = StateManager.get('configData') as ConfigData | undefined;
    if (!unit || !config?.abilities || !config?.masteries) {
      browser.innerHTML = '<p style="text-align:center;opacity:0.3;padding:24px">No data</p>';
      return;
    }

    const unlockedSet = new Set((unit.unlockedSkills ?? []).map(s => s.id));
    const unlockedMap = new Map((unit.unlockedSkills ?? []).map(s => [s.id, s]));
    const equippedMap: Record<string, number> = {};
    SKILL_SLOT_KEYS.forEach((key, i) => {
      const id = unit[key];
      if (id) equippedMap[id] = i;
    });

    // Get strand's mastery costs to filter accessible masteries
    const masteryCosts: Record<string, number> = (() => {
      try { return JSON.parse(unit.masteryCosts || '{}'); } catch { return {}; }
    })();
    const effectiveRanks: Record<string, number> = unit.computedStats?.masteryRanks ?? {};

    // Group ALL skills by mastery (unlocked + locked), filtered to accessible masteries
    type SkillEntry = { id: string; name: string; abilityId: string; rank: number; unlockReq: number; locked: boolean; reqText: string };
    const groups: Record<string, { mastery: MasteryDef; skills: SkillEntry[] }> = {};

    if (config.skillDefs) {
      for (const skill of Object.values(config.skillDefs)) {
        if (skill.internal) continue; // minion companion abilities are not equippable
        const r1Reqs = skill.ranks[0]?.masteryRequirements;
        if (!r1Reqs?.length) continue;
        const primaryId = r1Reqs[0].masteryId;
        const primaryReq = r1Reqs[0].rank;

        // Skip masteries this strand can't access (tier > Strong)
        if ((masteryCosts[primaryId] ?? 99) > 4) continue;

        const mastery = config.masteries[primaryId];
        if (!mastery) continue;
        if (!groups[primaryId]) groups[primaryId] = { mastery, skills: [] };

        const isUnlocked = unlockedSet.has(skill.id);
        const unlockedEntry = unlockedMap.get(skill.id);

        groups[primaryId].skills.push({
          id: skill.id,
          name: skill.name,
          abilityId: skill.abilityId,
          rank: unlockedEntry?.rank ?? 0,
          unlockReq: primaryReq,
          locked: !isUnlocked,
          reqText: !isUnlocked ? r1Reqs.map(r => `${config.masteries[r.masteryId]?.name ?? r.masteryId} R${r.rank}`).join(' + ') : '',
        });
      }
    }

    // Sort: groups by cost (cheapest first), skills by unlock rank, unlocked before locked
    const sortedGroupIds = Object.keys(groups)
      .filter(gid => {
        // Show if strand has investment OR any unlocked skills
        return (effectiveRanks[gid] ?? 0) > 0 || groups[gid].skills.some(s => !s.locked);
      })
      .sort((a, b) => (masteryCosts[a] ?? 8) - (masteryCosts[b] ?? 8));

    for (const gid of sortedGroupIds) {
      groups[gid].skills.sort((a, b) => {
        if (a.locked !== b.locked) return a.locked ? 1 : -1;
        return a.unlockReq - b.unlockReq;
      });
    }

    const groupsHtml = sortedGroupIds.map(gid => {
      const g = groups[gid];
      const rows = g.skills.map(entry => {
        const ability = config.abilities[entry.abilityId];
        if (!ability) return '';
        const iconSrc = getAbilityIconUrl(entry.abilityId);

        if (entry.locked) {
          return `<div class="sb-row locked" data-locked-id="${entry.abilityId}">
            <div class="sb-row-icon"><img src="${iconSrc}" alt="" width="24" height="24" draggable="false" onerror="this.style.display='none'"></div>
            <div class="sb-row-info">
              <span class="sb-row-name">${entry.name}</span>
              <span class="sb-row-req">${entry.reqText}</span>
            </div>
            <span class="sb-row-rank">R${entry.unlockReq}</span>
          </div>`;
        }

        const elCls = `element-${ability.element}`;
        const slotIdx = equippedMap[entry.id];
        const isEquipped = slotIdx !== undefined;
        const skillDef = config.skillDefs?.[entry.id];
        const rankData = skillDef?.ranks[entry.rank - 1];
        const hasDamage = !!rankData && (rankData.minDamage > 0 || rankData.maxDamage > 0);
        const dmgText = hasDamage ? `${rankData.minDamage}–${rankData.maxDamage}` : '';
        const dotText = rankData?.dotDamage ? `+${rankData.dotDamage} DoT` : '';
        const detail = [dmgText, dotText].filter(Boolean).join(' ');

        return `<div class="sb-row unlocked${isEquipped ? ' equipped' : ''}" data-equip-id="${entry.id}">
          <div class="sb-row-icon"><img src="${iconSrc}" alt="" width="24" height="24" draggable="false" onerror="this.style.display='none'"></div>
          <div class="sb-row-info">
            <span class="sb-row-name ${elCls}">${entry.name}</span>
            <span class="sb-row-type">${ability.type}${detail ? ` · ${detail}` : ''}</span>
          </div>
          ${isEquipped ? `<span class="sb-slot-badge">${slotIdx + 1}</span>` : ''}
          <span class="sb-row-rank">R${entry.rank}</span>
        </div>`;
      }).join('');

      return `<div class="sb-group">
        <div class="sb-group-header">${g.mastery.name}</div>
        <div class="sb-group-body">${rows}</div>
      </div>`;
    }).join('');

    const hasAny = sortedGroupIds.length > 0;
    const emptyMsg = !hasAny
      ? '<p class="sb-empty">Invest in masteries to unlock skills.</p>'
      : '';

    browser.innerHTML = `
      <div class="sb-header">
        <span class="sb-title">Skill Loadout</span>
        <button class="sb-close">&times;</button>
      </div>
      <div class="sb-body">
        ${emptyMsg}
        ${groupsHtml}
      </div>
    `;

    // Close button
    browser.querySelector('.sb-close')?.addEventListener('click', () => this.closeSkillBrowser());

    // Attach tooltips + equip click on unlocked rows
    browser.querySelectorAll('.sb-row.unlocked').forEach(el => {
      const abilityId = (el as HTMLElement).dataset.equipId!;
      const ability = config.abilities[abilityId];
      if (ability) {
        const skillDef = config.skillDefs?.[abilityId];
        const uEntry = unlockedMap.get(abilityId);
        const rankData = skillDef?.ranks[(uEntry?.rank ?? 1) - 1];
        Tooltip.attach(el as HTMLElement, () => {
          const elColor = elementColor(ability.element);
          const lines = buildSkillAbilityLines(ability, rankData, config, {
            unit,
            skillId: abilityId,
            rankCount: skillDef?.ranks.length,
          });
          return `<div class="tooltip-inner">
            <span class="tooltip-name" style="color:${elColor}">${uEntry?.name ?? ability.name}</span>
            ${lines.join('')}
          </div>`;
        }, { delay: 500 });
      }

      el.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (this.primedSkillSlot === null) return;
        const currentUnit = this.getSelectedUnit();
        if (!currentUnit) return;
        const result = await APIManager.equipSkill(currentUnit.id, abilityId, this.primedSkillSlot);
        if (result.success) {
          const stateResult = await APIManager.fetchUnifiedState();
          if (stateResult.success && stateResult.data) StateManager.batchUpdate(stateResult.data);
          this.closeSkillBrowser();
        } else {
          console.error('[SkillBrowser] equip failed:', result.error);
          const { ToastManager } = await import('../components/Toast');
          ToastManager.show({ type: 'error', message: result.error ?? 'Failed to equip skill' });
        }
      });
    });

    // Attach tooltips to locked rows (R1 rank data preview)
    browser.querySelectorAll('.sb-row.locked').forEach(el => {
      const abilityId = (el as HTMLElement).dataset.lockedId!;
      const ability = config.abilities[abilityId];
      if (ability) {
        const skillDef = config.skillDefs?.[abilityId];
        const rankData = skillDef?.ranks[0]; // show R1 preview
        Tooltip.attach(el as HTMLElement, () => {
          const elColor = elementColor(ability.element);
          const lines = buildSkillAbilityLines(ability, rankData, config, {
            skillId: abilityId,
            rankCount: skillDef?.ranks.length,
          });
          return `<div class="tooltip-inner">
            <span class="tooltip-name" style="color:${elColor}">${skillDef?.name ?? ability.name}</span>
            ${lines.join('')}
          </div>`;
        }, { delay: 500 });
      }
    });
  }

  // ─── Equipment Slots ───

  private renderEquipSlots(): void {
    const slotsEl = this.$('.equip-slots');
    if (!slotsEl) return;

    const unit = this.getSelectedUnit();
    const itemData = StateManager.get('itemData') as ItemData[] | undefined;
    const configData = StateManager.get('configData') as ConfigData | undefined;

    if (!unit) {
      slotsEl.innerHTML = '';
      return;
    }

    const weapon = itemData?.find(i => i.id === unit.weaponId) ?? null;
    const armor = itemData?.find(i => i.id === unit.armorId) ?? null;
    const offhand = itemData?.find(i => i.id === unit.offhandId) ?? null;
    const accessory = itemData?.find(i => i.id === unit.accessoryId) ?? null;

    slotsEl.innerHTML =
      renderEquipCard('weapon', 'Weapon', weapon, { draggable: true }) +
      renderEquipCard('armor', 'Armor', armor, { draggable: true }) +
      renderEquipCard('offhand', 'Offhand', offhand, { draggable: true }) +
      renderEquipCard('accessory', 'Accessory', accessory, { draggable: true });

    slotsEl.querySelectorAll('.equip-card').forEach(el => {
      const cardEl = el as HTMLElement;
      const slot = cardEl.dataset.slot as 'weapon' | 'armor' | 'offhand' | 'accessory';
      const isFilled = cardEl.classList.contains('filled');
      const item = slot === 'weapon' ? weapon : slot === 'armor' ? armor : slot === 'offhand' ? offhand : accessory;

      // Tooltip
      if (item) {
        Tooltip.attach(cardEl, () => buildItemTooltip(item, configData, unit));
      }

      // Click filled → show detail bar with unequip option; click empty → scroll to inventory
      cardEl.addEventListener('click', () => {
        if (isFilled && item) {
          this.selectEquippedItem(cardEl, item, slot, unit, configData);
        } else {
          this.$('.inventory-grid')?.scrollIntoView({ behavior: 'smooth' });
        }
      });

      // Drag from filled slot → unequip
      if (isFilled) {
        cardEl.addEventListener('dragstart', (e) => {
          this.dragSource = { type: 'equip-slot', slot };
          cardEl.classList.add('dragging');
          (e as DragEvent).dataTransfer!.effectAllowed = 'move';
        });
        cardEl.addEventListener('dragend', () => {
          cardEl.classList.remove('dragging');
          this.dragSource = null;
        });
      }
    });

    // Drop zone: dragging an inventory item anywhere over the equip area highlights
    // the matching-category card and routes the drop there. Bind parent-level
    // listeners ONCE (slotsEl persists across renders; only its children get
    // replaced). Read getSelectedUnit() inside the handler so the drop always
    // routes to the currently selected strand, not whichever was active on the
    // render that bound the listener.
    interface BoundSlotsEl extends HTMLElement { __equipDropBound?: boolean }
    const slotsElBound = slotsEl as BoundSlotsEl;
    if (!slotsElBound.__equipDropBound) {
      slotsElBound.__equipDropBound = true;
      const clearHighlights = () => {
        slotsEl.querySelectorAll('.equip-card.drop-target').forEach(c => c.classList.remove('drop-target'));
      };
      const matchingCard = (): HTMLElement | null => {
        if (this.dragSource?.type !== 'inventory-item') return null;
        return slotsEl.querySelector<HTMLElement>(`.equip-card[data-slot="${this.dragSource.category}"]`);
      };

      slotsEl.addEventListener('dragover', (e) => {
        const target = matchingCard();
        if (!target) return;
        e.preventDefault();
        (e as DragEvent).dataTransfer!.dropEffect = 'move';
        slotsEl.querySelectorAll('.equip-card.drop-target').forEach(c => {
          if (c !== target) c.classList.remove('drop-target');
        });
        target.classList.add('drop-target');
      });
      slotsEl.addEventListener('dragleave', (e) => {
        const related = (e as DragEvent).relatedTarget as Node | null;
        if (related && slotsEl.contains(related)) return;
        clearHighlights();
      });
      slotsEl.addEventListener('drop', (e) => {
        if (this.dragSource?.type !== 'inventory-item') return;
        e.preventDefault();
        clearHighlights();
        const currentUnit = this.getSelectedUnit();
        if (!currentUnit) return;
        this.equipItemToUnit(currentUnit.id, this.dragSource.itemId);
        this.dragSource = null;
      });
    }
  }

  // ─── Inventory Grid (fixed 30-slot grid) ───

  private static readonly INVENTORY_SIZE = 30;

  private renderInventoryGrid(): void {
    const gridEl = this.$('.inventory-grid');
    if (!gridEl) return;

    const unit = this.getSelectedUnit();
    const configData = StateManager.get('configData') as ConfigData | undefined;
    const itemData = StateManager.get('itemData') as ItemData[] | undefined;

    const itemSlotMap = new Map<number, ItemData>();
    for (const item of (itemData ?? [])) {
      if (!item.equippedToUnitId && item.inventorySlot != null) {
        itemSlotMap.set(item.inventorySlot, item);
      }
    }

    const stripRarity = (name: string) => name.replace(/^(Magic|Rare|Epic|Legendary)\s+/i, '');

    const maxSlots = PartyScreen.INVENTORY_SIZE;

    // Render fixed grid
    const cells: string[] = [];
    for (let i = 0; i < maxSlots; i++) {
      const item = itemSlotMap.get(i);
      if (!item) {
        cells.push(`<div class="inv-slot inv-slot-empty" data-inv-slot="${i}"></div>`);
        continue;
      }
      const rColor = rarityColor(item.rarity ?? 'normal');
      const unequippable = unit ? (!this.unitMeetsMasteryReqs(unit, configData?.itemBases?.[item.baseType]?.masteryRequirements) || this.isHandednessBlocked(unit, item, configData)) : false;
      cells.push(`
        <div class="inv-slot inv-card${unequippable ? ' inv-unequippable' : ''}" data-inv-slot="${i}" data-item-id="${item.id}" data-category="${item.category}" draggable="true" style="border-color:${rColor}">
          ${renderItemIcon(item.baseType ?? '', item.category, 'md')}
          <span class="inv-card-name" style="color:${rColor}">${stripRarity(item.name)}</span>
          <button class="inv-card-delete" data-item-id="${item.id}" type="button" title="Salvage">\u267B</button>
        </div>
      `);
    }

    gridEl.innerHTML = cells.join('');

    // Bind all slots for drag-drop reordering
    gridEl.querySelectorAll('.inv-slot').forEach(el => {
      const slotEl = el as HTMLElement;
      const slotIdx = Number(slotEl.dataset.invSlot);

      // Drop target for inventory reorder + unequip
      slotEl.addEventListener('dragover', (e) => {
        const src = this.dragSource;
        if (!src) return;
        if (src.type === 'inventory-item' || src.type === 'equip-slot') {
          e.preventDefault();
          (e as DragEvent).dataTransfer!.dropEffect = 'move';
          slotEl.classList.add('drop-target');
        }
      });
      slotEl.addEventListener('dragleave', () => slotEl.classList.remove('drop-target'));
      slotEl.addEventListener('drop', (e) => {
        e.preventDefault();
        slotEl.classList.remove('drop-target');
        if (!this.dragSource || !this.selectedUnitId) return;
        if (this.dragSource.type === 'inventory-item') {
          this.moveInventorySlot('item', this.dragSource.itemId, slotIdx);
        } else if (this.dragSource.type === 'equip-slot') {
          this.unequipSlot(this.selectedUnitId, this.dragSource.slot);
        }
        this.dragSource = null;
      });
    });

    const canHover = window.matchMedia('(hover: hover)').matches;

    // Bind filled item cards
    gridEl.querySelectorAll('.inv-card[data-item-id]').forEach(el => {
      const cardEl = el as HTMLElement;
      const itemId = cardEl.dataset.itemId!;
      const invSlot = Number(cardEl.dataset.invSlot);
      const item = itemSlotMap.get(invSlot);

      if (item && canHover) Tooltip.attach(cardEl, () => buildItemTooltip(item, configData, unit));

      cardEl.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).classList.contains('inv-card-delete')) return;
        if (!canHover && item) {
          this.selectInventoryItem(cardEl, item, configData, unit ?? undefined);
          return;
        }
        if (unit) this.equipItemToUnit(unit.id, itemId);
      });

      cardEl.addEventListener('dragstart', (e) => {
        const category = (item?.category ?? 'weapon') as 'weapon' | 'armor' | 'accessory' | 'offhand';
        this.dragSource = { type: 'inventory-item', itemId, invSlot, category };
        cardEl.classList.add('dragging');
        (e as DragEvent).dataTransfer!.effectAllowed = 'move';
      });
      cardEl.addEventListener('dragend', () => {
        cardEl.classList.remove('dragging');
        this.dragSource = null;
      });
    });

    // Delete buttons — items
    gridEl.querySelectorAll('.inv-card-delete[data-item-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = (btn as HTMLElement).dataset.itemId!;
        const item = [...itemSlotMap.values()].find(v => v.id === itemId);
        if (item) this.confirmDeleteItem(item);
      });
    });

  }

  // ─── Inline unequip confirm ───

  private showUnequipConfirm(cardEl: HTMLElement, onConfirm: () => void): void {
    if (cardEl.querySelector('.unequip-confirm')) return;

    const confirm = document.createElement('div');
    confirm.className = 'unequip-confirm';
    confirm.innerHTML = `
      <span>Unequip?</span>
      <button class="unequip-yes" type="button">Yes</button>
      <button class="unequip-no" type="button">No</button>
    `;
    cardEl.appendChild(confirm);

    confirm.querySelector('.unequip-yes')?.addEventListener('click', (e) => {
      e.stopPropagation();
      onConfirm();
    });
    confirm.querySelector('.unequip-no')?.addEventListener('click', (e) => {
      e.stopPropagation();
      confirm.remove();
    });
  }

  // ─── Equip slot tap → detail bar ───

  private selectEquippedItem(cardEl: HTMLElement, item: ItemData, slot: 'weapon' | 'armor' | 'accessory' | 'offhand', unit: PartyUnit, configData?: ConfigData): void {
    // Highlight the tapped equip card
    this.rootEl?.querySelectorAll('.equip-card.equip-selected').forEach(el => el.classList.remove('equip-selected'));
    cardEl.classList.add('equip-selected');

    const bar = this.$('.inv-detail-bar');
    if (!bar) return;

    const rColor = rarityColor(item.rarity ?? 'normal');
    const categoryLabel = formatCategory(item.category);

    bar.innerHTML = `
      <div class="inv-detail-bar-icon">${renderItemIcon(item.baseType ?? '', item.category, 'md')}</div>
      <div class="inv-detail-bar-info">
        <span class="inv-detail-bar-name" style="color:${rColor}">${item.name}</span>
        <span class="inv-detail-bar-sub">${categoryLabel}</span>
      </div>
      <div class="inv-detail-bar-actions">
        <button class="inv-detail-btn-unequip" type="button">Unequip</button>
      </div>
    `;
    bar.classList.add('visible');

    bar.querySelector('.inv-detail-btn-unequip')?.addEventListener('click', () => {
      this.unequipSlot(unit.id, slot);
      bar.classList.remove('visible');
      cardEl.classList.remove('equip-selected');
    });
  }

  // ─── Mobile tap-to-select detail bar ───

  private selectInventoryItem(cardEl: HTMLElement, item: ItemData, configData?: ConfigData, unit?: PartyUnit): void {
    // Deselect previous
    this.rootEl?.querySelectorAll('.inv-card.inv-selected').forEach(el => el.classList.remove('inv-selected'));
    cardEl.classList.add('inv-selected');

    const bar = this.$('.inv-detail-bar');
    if (!bar) return;

    const rColor = rarityColor(item.rarity ?? 'normal');
    const affixes = this.itemAffixSummary(item);
    const categoryLabel = formatCategory(item.category);

    bar.innerHTML = `
      <div class="inv-detail-bar-icon">${renderItemIcon(item.baseType ?? '', item.category, 'md')}</div>
      <div class="inv-detail-bar-info">
        <span class="inv-detail-bar-name" style="color:${rColor}">${item.name}</span>
        <span class="inv-detail-bar-sub">${categoryLabel}</span>
        ${affixes ? `<span class="inv-detail-bar-affixes">${affixes}</span>` : ''}
      </div>
      <div class="inv-detail-bar-actions">
        ${unit ? `<button class="inv-detail-btn-equip" data-item-id="${item.id}" type="button">Equip</button>` : ''}
        <button class="inv-detail-btn-delete" data-item-id="${item.id}" type="button">Salvage</button>
      </div>
    `;
    bar.classList.add('visible');

    // Bind actions
    bar.querySelector('.inv-detail-btn-equip')?.addEventListener('click', () => {
      if (unit) this.equipItemToUnit(unit.id, item.id);
      bar.classList.remove('visible');
    });
    bar.querySelector('.inv-detail-btn-delete')?.addEventListener('click', () => {
      this.confirmDeleteItem(item);
      bar.classList.remove('visible');
    });
  }

  // ─── Inline delete confirm ───

  private confirmDeleteItem(item: ItemData): void {
    const card = this.rootEl?.querySelector(`.inv-card[data-item-id="${item.id}"]`);
    if (!card || card.querySelector('.delete-confirm')) return;

    const rarity = item.rarity ?? 'normal';
    const rewardLabel: Record<string, string> = {
      normal: '+1 Lustrous Yarn',
      magic: '+1 Vivid Spool',
      rare: '+1 Prismatic Spool',
      unique: '+1 Binding',
    };
    const reward = rewardLabel[rarity] ?? '+1 Vivid Spool';
    const isUnique = rarity === 'unique';

    const confirm = document.createElement('div');
    confirm.className = 'delete-confirm' + (isUnique ? ' delete-confirm--unique' : '');
    if (isUnique) {
      confirm.innerHTML = `
        <span class="delete-confirm-title">Destroy ${item.name}?</span>
        <span class="salvage-reward">${reward}</span>
        <div class="delete-confirm-buttons">
          <button class="delete-confirm-yes" type="button">Destroy</button>
          <button class="delete-confirm-no" type="button">Cancel</button>
        </div>
      `;
    } else {
      confirm.innerHTML = `
        <span>Salvage?</span>
        <span class="salvage-reward">${reward}</span>
        <div class="delete-confirm-buttons">
          <button class="delete-confirm-yes" type="button">Salvage</button>
          <button class="delete-confirm-no" type="button">Cancel</button>
        </div>
      `;
    }
    card.appendChild(confirm);

    confirm.querySelector('.delete-confirm-yes')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.deleteItem(item.id);
    });
    confirm.querySelector('.delete-confirm-no')?.addEventListener('click', (e) => {
      e.stopPropagation();
      confirm.remove();
    });
  }

  // ─── Actions ───

  private async equipItemToUnit(unitId: string, itemId: string): Promise<void> {
    const result = await APIManager.equipItem(unitId, itemId);
    if (result.success) await this.refreshState();
    else EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Equip failed' });
  }

  private async unequipSlot(unitId: string, slot: 'weapon' | 'armor' | 'accessory' | 'offhand'): Promise<void> {
    const result = await APIManager.unequipItem(unitId, slot);
    if (result.success) await this.refreshState();
    else EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Unequip failed' });
  }

  private async deleteItem(itemId: string): Promise<void> {
    const result = await APIManager.deleteItem(itemId);
    if (result.success) await this.refreshState();
    else EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Salvage failed' });
  }

  private async moveInventorySlot(type: 'item', id: string, toSlot: number): Promise<void> {
    const result = await APIManager.moveInventorySlot(type, id, toSlot);
    if (result.success) await this.refreshState();
    else EventBus.emit(Events.SHOW_TOAST, { type: 'error', message: result.error ?? 'Move failed' });
  }

  private async refreshState(): Promise<void> {
    const stateResult = await APIManager.fetchUnifiedState();
    if (stateResult.success && stateResult.data) {
      StateManager.batchUpdate({
        playerData: stateResult.data.playerData,
        unitData: stateResult.data.unitData,
        itemData: stateResult.data.itemData,
      });
    }
  }

  private clearDropHighlights(): void {
    this.rootEl?.querySelectorAll('.drop-target, .drop-target-invalid').forEach(el => {
      el.classList.remove('drop-target', 'drop-target-invalid');
    });
  }
}
